import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw, CheckCircle2, AlertCircle, PauseCircle, Copy } from "lucide-react";

/**
 * Admin viewer for the last N `email_send_log` rows. Populated by
 * utils/email_sender.py on every register / resend / password-reset send.
 * Statuses:
 *   accepted            — Resend accepted the send (returned an id)
 *   delivered           — legacy label for provider acceptance
 *   failed              — Resend threw (bad key, unverified domain, etc.)
 *   skipped_no_api_key  — RESEND_API_KEY was missing in that environment
 */
export default function EmailSendLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email-send-log?limit=100");
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load email log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!expanded && rows.length === 0 && !loading) return null;

  const statusColor = (s) => {
    if (s === "accepted" || s === "delivered") return "text-emerald-700 dark:text-emerald-400";
    if (s === "failed")             return "text-red-600 dark:text-red-400";
    if (s === "skipped_no_api_key") return "text-amber-700 dark:text-amber-500";
    return "text-slate-500 dark:text-slate-400";
  };
  const statusIcon = (s) => {
    if (s === "accepted" || s === "delivered") return <CheckCircle2 className="w-3.5 h-3.5"/>;
    if (s === "failed")             return <AlertCircle className="w-3.5 h-3.5"/>;
    if (s === "skipped_no_api_key") return <PauseCircle className="w-3.5 h-3.5"/>;
    return null;
  };

  const shown = expanded ? rows : rows.slice(0, 5);

  return (
    <div
      data-testid="email-send-log-panel"
      className="mb-6 border border-slate-200 dark:border-slate-800 rounded-lg bg-white dark:bg-slate-900"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-800">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Email send log</div>
          <div className="text-[11px] text-slate-500 dark:text-slate-500">Last {rows.length} transactional send attempts (Resend). Newest first.</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="email-log-refresh"
            variant="outline" size="sm" onClick={load} disabled={loading}
            className="border-slate-300 dark:border-slate-700"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`}/>Refresh
          </Button>
          {rows.length > 5 && (
            <Button
              variant="outline" size="sm" onClick={() => setExpanded(v => !v)}
              className="border-slate-300 dark:border-slate-700"
            >
              {expanded ? "Collapse" : `Show all ${rows.length}`}
            </Button>
          )}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400 italic text-center">
          No email attempts recorded yet.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800 max-h-96 overflow-y-auto">
          {shown.map((r, i) => (
            <li key={i} data-testid={`email-log-row-${i}`} className="px-4 py-2 text-sm flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold ${statusColor(r.status)}`}>
                {statusIcon(r.status)}
                {r.status === "delivered" ? "accepted" : r.status}
              </span>
              <span className="font-mono text-xs text-slate-800 dark:text-slate-200 truncate max-w-[220px]" title={r.to}>{r.to}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 flex-1 truncate" title={r.subject}>{r.subject}</span>
              <span className="text-[11px] text-slate-400 dark:text-slate-500 font-mono">
                from {r.sender}
              </span>
              <span className="text-[11px] text-slate-400 dark:text-slate-500 font-mono">
                {new Date(r.attempted_at).toLocaleString()}
              </span>
              {r.error && (
                <div className="basis-full pl-6 pt-1 text-[11px] text-red-600 dark:text-red-400 font-mono flex items-center gap-2">
                  <span className="truncate flex-1" title={r.error}>{r.error}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(r.error).then(() => toast.success("Copied error"))}
                    className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    title="Copy error to clipboard"
                  >
                    <Copy className="w-3 h-3"/>
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
